// ─── planner/plannerUtils.js — constants and pure helpers ──────────────────
// Imported by Planner.jsx, PlannerWizard.jsx, PlannerTimeline.jsx

import { mkKey } from "../../lib/calculations.js";
import { MONTH_NAMES } from "../../lib/calculations.js";

// ── Categories ─────────────────────────────────────────────────────────────────
export const CATEGORIES = [
  { id:"marketplace", label:"Marketplace", color:"#0071e3", icon:"🏪" },
  { id:"product",     label:"Product",     color:"#34c759", icon:"📦" },
  { id:"logistics",   label:"Logistics",   color:"#ff9500", icon:"🚚" },
  { id:"finance",     label:"Finance",     color:"#bf5af2", icon:"💰" },
  { id:"marketing",   label:"Marketing",   color:"#ff375f", icon:"📣" },
  { id:"operations",  label:"Operations",  color:"#5ac8fa", icon:"⚙️" },
];

// ── Example decisions ──────────────────────────────────────────────────────────
export const EXAMPLES = [
  { title:"Launch on Amazon France", cat:"marketplace", desc:"Expand to FR marketplace, new payout stream from June" },
  { title:"Switch 3PL / Warehouse", cat:"logistics",    desc:"Move from 3PL A to 3PL B, cost changes from handover date" },
  { title:"Raise product price +15%", cat:"product",    desc:"Increase price across all SKUs, affects payout per unit" },
  { title:"Run Q4 Promo Campaign",   cat:"marketing",   desc:"20% discount Nov–Dec, reduces net payout but boosts units" },
  { title:"Hire first VA / employee", cat:"operations", desc:"Monthly salary cost starting from onboarding date" },
  { title:"Take out business loan",  cat:"finance",     desc:"Lump sum received + monthly repayment expense" },
  { title:"Discontinue slow SKU",    cat:"product",     desc:"Stop selling product, removes units + income from date" },
  { title:"Move FBA → FBM",         cat:"logistics",   desc:"Changes fulfilment cost structure and payout delay" },
  { title:"VAT registration EU",     cat:"finance",     desc:"Quarterly VAT payment obligation starting next quarter" },
  { title:"Negotiate supplier terms",cat:"finance",     desc:"Deposit drops 30%→20%, improves cashflow timing" },
];

// ── Impact types ──────────────────────────────────────────────────────────────
export const IMPACT_TYPES = [
  { id:"income_pct",    icon:"📈", label:"Income change (%)", desc:"Payout per unit goes up or down — price change, promotion, marketplace fee change" },
  { id:"units_pct",     icon:"📊", label:"Units sold change (%)", desc:"Sell more or fewer units — stockout, campaign, new market, discontinue SKU" },
  { id:"new_expense",   icon:"💸", label:"New recurring expense", desc:"Ongoing cost starting from this date — salary, rent, subscription, 3PL fee" },
  { id:"one_time_cost", icon:"🔖", label:"One-time cost", desc:"Single payment — setup fee, inspection, VAT payment, equipment purchase" },
  { id:"cash_in",       icon:"💵", label:"Cash received (one-time)", desc:"Loan, rebate, grant, insurance payout, refund" },
  { id:"loan",          icon:"🏦", label:"Business loan", desc:"Lump sum in + monthly repayment. We create both entries automatically." },
  { id:"stop_selling",  icon:"🛑", label:"Stop selling / discontinue", desc:"Remove units and income entirely from selected date" },
  { id:"payment_terms", icon:"📅", label:"Payment terms change", desc:"Supplier deposit % changes, affects when cash leaves your account" },
];

export const RECURRENCE_OPTS = [
  { id:"once",    label:"One-time",     desc:"Happens once on the selected date" },
  { id:"ongoing", label:"Ongoing",      desc:"Continues from this date forward indefinitely" },
  { id:"period",  label:"Fixed period", desc:"From date X to date Y" },
  { id:"yearly",  label:"Yearly",       desc:"Repeats every year on this date" },
  { id:"quarterly",label:"Quarterly",   desc:"Repeats every 3 months" },
];

export const DATE_PREC = [
  { id:"day",     label:"Exact day" },
  { id:"month",   label:"Month" },
  { id:"quarter", label:"Quarter" },
];

export function catInfo(id) { return CATEGORIES.find(c => c.id === id) || CATEGORIES[0]; }

// ── Priority levels ──────────────────────────────────────────────────────────
export const PRIORITIES = [
  { id:"must",   label:"Must Do",      icon:"🔴", color:"#ef4444", weight:3 },
  { id:"should", label:"Should Do",    icon:"🟡", color:"#ff9500", weight:2 },
  { id:"nice",   label:"Nice to Have", icon:"🟢", color:"#34c759", weight:1 },
];
export function priorityInfo(id) { return PRIORITIES.find(p => p.id === id) || PRIORITIES[1]; }
export function priorityRadius(d) { const p = (d?.priority || "should"); return p === "must" ? 5.5 : p === "nice" ? 2.5 : 3.5; }
export function milestonePct(d) { if (!d?.milestones?.length) return null; return d.milestones.filter(m=>m.done).length / d.milestones.length; }

// ── Decision templates ───────────────────────────────────────────────────────
export const DECISION_TEMPLATES = [
  { id:"tpl_launch_product", name:"Launch New Product (Amazon)", category:"marketplace", icon:"🚀", color:"#0071e3", priority:"must",
    impacts:[{type:"units_pct",pct:0,id:"t1"},{type:"one_time_cost",what:"Product launch costs",amount:0,id:"t2"}],
    milestones:[{id:"m1",title:"Product listing created",done:false},{id:"m2",title:"Inventory received at FBA",done:false},{id:"m3",title:"PPC campaign launched",done:false},{id:"m4",title:"First reviews received",done:false}],
    notes:"Launch plan for a new product on Amazon. Fill in the expected unit increase % and one-time launch costs (photography, samples, etc)." },
  { id:"tpl_new_marketplace", name:"Expand to New Marketplace", category:"marketplace", icon:"🌍", color:"#5ac8fa", priority:"must",
    impacts:[{type:"income_pct",pct:0,mktScope:"all",id:"t1"},{type:"new_expense",what:"Marketplace setup costs",amount:0,dayOfMonth:1,id:"t2"}],
    milestones:[{id:"m1",title:"Register seller account",done:false},{id:"m2",title:"Translate/adapt listings",done:false},{id:"m3",title:"Ship inventory",done:false},{id:"m4",title:"Go live",done:false}],
    notes:"Expansion to a new marketplace (Walmart, Shopify, Amazon EU). Set the expected income increase and any recurring costs." },
  { id:"tpl_reorder", name:"Reorder Inventory", category:"logistics", icon:"📦", color:"#ff9500", priority:"must",
    impacts:[{type:"one_time_cost",what:"Supplier deposit",amount:0,id:"t1"}],
    milestones:[{id:"m1",title:"Negotiate with supplier",done:false},{id:"m2",title:"Place order & pay deposit",done:false},{id:"m3",title:"Track production",done:false},{id:"m4",title:"Goods received & checked in",done:false}],
    notes:"Inventory replenishment order. Enter the deposit amount. Create the full order in Supplier Orders for payment term tracking." },
  { id:"tpl_promo", name:"Run Promotional Campaign", category:"marketing", icon:"📣", color:"#ff375f", priority:"should",
    impacts:[{type:"units_pct",pct:20,id:"t1"},{type:"income_pct",pct:-15,mktScope:"all",id:"t2"}],
    milestones:[{id:"m1",title:"Plan campaign strategy",done:false},{id:"m2",title:"Create ad creatives",done:false},{id:"m3",title:"Launch campaign",done:false},{id:"m4",title:"Analyze results",done:false}],
    notes:"Promotional campaign (Black Friday, seasonal sale, coupon). Typically increases units but reduces payout per unit due to discounts." },
  { id:"tpl_hire_va", name:"Hire VA / Employee", category:"operations", icon:"👤", color:"#5ac8fa", priority:"should",
    impacts:[{type:"new_expense",what:"VA/Employee salary",amount:0,dayOfMonth:1,id:"t1"}],
    milestones:[{id:"m1",title:"Post job listing",done:false},{id:"m2",title:"Interview candidates",done:false},{id:"m3",title:"Onboard & train",done:false},{id:"m4",title:"Fully operational",done:false}],
    notes:"Hiring a virtual assistant or employee. Enter the monthly salary/cost. Consider the impact on your capacity and sales growth." },
  { id:"tpl_loan", name:"Take Business Loan", category:"finance", icon:"🏦", color:"#bf5af2", priority:"should",
    impacts:[{type:"loan",what:"Business loan",loanAmount:0,repayment:0,durationMonths:12,id:"t1"}],
    milestones:[{id:"m1",title:"Compare lender rates",done:false},{id:"m2",title:"Apply & get approved",done:false},{id:"m3",title:"Receive funds",done:false},{id:"m4",title:"Set up repayment schedule",done:false}],
    notes:"Business loan or credit line. Enter the lump sum received and monthly repayment amount." },
  { id:"tpl_switch_fulfillment", name:"Switch Fulfillment Method", category:"logistics", icon:"🔄", color:"#ff9500", priority:"should",
    impacts:[{type:"new_expense",what:"New fulfillment cost delta",amount:0,dayOfMonth:1,id:"t1"}],
    milestones:[{id:"m1",title:"Compare fulfillment costs",done:false},{id:"m2",title:"Test new provider",done:false},{id:"m3",title:"Migrate inventory",done:false},{id:"m4",title:"Verify operations stable",done:false}],
    notes:"Switching between FBA, FBM, or 3PL. Enter the net cost difference (positive if more expensive, negative if saving)." },
  { id:"tpl_discontinue", name:"Discontinue Slow SKU", category:"product", icon:"🛑", color:"#ef4444", priority:"should",
    impacts:[{type:"stop_selling",mktScope:"all",id:"t1"}],
    milestones:[{id:"m1",title:"Review sales data & margin",done:false},{id:"m2",title:"Liquidate remaining stock",done:false},{id:"m3",title:"Delist from marketplace",done:false},{id:"m4",title:"Close supplier relationship",done:false}],
    notes:"Discontinuing a product that isn't performing. This stops all unit projections from the selected date." },
  { id:"tpl_negotiate_supplier", name:"Negotiate Supplier Terms", category:"finance", icon:"🤝", color:"#bf5af2", priority:"nice",
    impacts:[{type:"payment_terms",orderIds:[],oldDeposit:30,newDeposit:20,id:"t1"}],
    milestones:[{id:"m1",title:"Research industry benchmarks",done:false},{id:"m2",title:"Negotiate with supplier",done:false},{id:"m3",title:"Sign new agreement",done:false},{id:"m4",title:"Verify first order on new terms",done:false}],
    notes:"Renegotiating payment terms with your supplier. A lower deposit % improves your short-term cashflow." },
  { id:"tpl_vat", name:"Register for VAT/Tax", category:"finance", icon:"🏛", color:"#bf5af2", priority:"must",
    impacts:[{type:"new_expense",what:"Quarterly VAT payment",amount:0,dayOfMonth:15,id:"t1"}],
    milestones:[{id:"m1",title:"Check VAT thresholds",done:false},{id:"m2",title:"Apply for registration",done:false},{id:"m3",title:"Set up accounting",done:false},{id:"m4",title:"First filing submitted",done:false}],
    notes:"VAT/tax registration in a new country. Enter the estimated quarterly or monthly tax payment as a recurring expense." },
];

// ── ROI computation ──────────────────────────────────────────────────────────
export function computeDecisionROI(decision, months, recurringExpenses, marketplaces) {
  if (!decision || decision.status !== "done") return null;
  const decDate = parsePlannerDate(decision.date);
  if (!decDate) return null;
  const decKey = mkKeyFromDate(decDate);

  // Expected monthly from impacts
  let expectedMonthly = 0;
  for (const imp of (decision.impacts || [])) {
    if (imp.type === "new_expense") expectedMonthly -= (imp.amount || 0);
    if (imp.type === "loan") expectedMonthly -= (imp.repayment || 0);
  }

  // Actual: count months since decision, sum linked recurring expenses spent
  const [dy, dm] = (decKey || "2025-1").split("-").map(Number);
  const now = new Date();
  const monthsElapsed = (now.getFullYear() - dy) * 12 + (now.getMonth() + 1 - dm);
  const linkedRec = (recurringExpenses || []).filter(r => r.decisionId === decision.id);
  const actualMonthlySpent = linkedRec.reduce((s, r) => s + (r.amount || 0), 0);
  const totalSpent = actualMonthlySpent * Math.max(1, monthsElapsed);
  const totalExpected = Math.abs(expectedMonthly) * Math.max(1, monthsElapsed);

  // For income/units impacts, compare actuals before vs after
  let incomeVariance = null;
  const incImpacts = (decision.impacts || []).filter(i => i.type === "income_pct" || i.type === "units_pct");
  if (incImpacts.length > 0 && monthsElapsed >= 2) {
    // Average actualSold in 3 months before decision vs 3 months after
    let beforeSum = 0, beforeCt = 0, afterSum = 0, afterCt = 0;
    for (let i = -3; i < 0; i++) { let m2 = dm + i, y2 = dy; if (m2 < 1) { m2 += 12; y2--; } const k = mkKey(y2, m2); const a = months[k]?.actualSold; if (a > 0) { beforeSum += a; beforeCt++; } }
    for (let i = 1; i <= 3; i++) { let m2 = dm + i, y2 = dy; if (m2 > 12) { m2 -= 12; y2++; } const k = mkKey(y2, m2); const a = months[k]?.actualSold; if (a > 0) { afterSum += a; afterCt++; } }
    if (beforeCt > 0 && afterCt > 0) {
      const avgBefore = beforeSum / beforeCt, avgAfter = afterSum / afterCt;
      incomeVariance = { before: Math.round(avgBefore), after: Math.round(avgAfter), changePct: Math.round(((avgAfter - avgBefore) / avgBefore) * 100) };
    }
  }

  return { expectedMonthly, actualMonthlySpent, totalSpent, totalExpected, monthsElapsed, incomeVariance };
}

// ── Helpers ───────────────────────────────────────────────────────────────────
export function parsePlannerDate(d) {
  if (!d) return null;
  if (d.prec === "day" && d.day) {
    const parts = d.day.split(".");
    if (parts.length === 3) return new Date(+parts[2], +parts[1]-1, +parts[0]);
  }
  if (d.prec === "month" && d.month) {
    const [y,m] = d.month.split("-").map(Number);
    return new Date(y, m-1, 1);
  }
  if (d.prec === "quarter" && d.quarter) {
    const [y,q] = d.quarter.split("-Q").map(Number);
    return new Date(y, (q-1)*3, 1);
  }
  return null;
}

export function dateLabel(d) {
  if (!d) return "—";
  const dt = parsePlannerDate(d);
  if (!dt) return "—";
  if (d.prec === "day") return `${dt.getDate()} ${MONTH_NAMES[dt.getMonth()]} ${dt.getFullYear()}`;
  if (d.prec === "month") return `${MONTH_NAMES[dt.getMonth()]} ${dt.getFullYear()}`;
  if (d.prec === "quarter") {
    const q = Math.floor(dt.getMonth()/3)+1;
    return `Q${q} ${dt.getFullYear()}`;
  }
  return "—";
}

export function mkKeyFromDate(dt) {
  if (!dt) return null;
  return mkKey(dt.getFullYear(), dt.getMonth()+1);
}

